import { request } from './_request.js';
import type { MyDleScoresResponse } from '../../types/dle-evaluation-score.js';
import type {
  MyDleTasksResponse,
  SubmitDleAnswerInput,
  SubmitDleAnswerResponse,
} from '../../types/dle-evaluation.js';

/**
 * DLE 評価アンケート API クライアント。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business DLE 評価アンケートの未回答タスク取得と回答登録を行う API クライアント
 * @graph-connects /dle-evaluation/my-tasks [calls, via:GET] 自分が回答すべき DLE 評価一覧を取得
 * @graph-connects /dle-evaluation/answers [calls, via:POST] DLE 評価回答を登録
 * @graph-connects /dle-evaluation/my-scores [calls, via:GET] 自分の DLE 評価集計と全体平均を取得
 * @graph-connects /dle-evaluation/scores [calls, via:GET] 指定した DLE の評価集計を取得（閲覧権限必須）
 */
export const dleEvaluationApi = {
  /** 自分が PL / 主担当である DLE 評価対象を未回答優先で取得する。 */
  getMyTasks: async (): Promise<MyDleTasksResponse> =>
    request<MyDleTasksResponse>('/dle-evaluation/my-tasks'),

  /** DLE 評価回答を登録する（1 母票につき 1 回のみ）。 */
  submitAnswer: async (payload: SubmitDleAnswerInput): Promise<SubmitDleAnswerResponse> =>
    request<SubmitDleAnswerResponse>('/dle-evaluation/answers', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  /** 自分が DLE として受けた評価の集計 (月次 / 四半期) と全体 DLE 平均を取得する。 */
  getMyScores: async (): Promise<MyDleScoresResponse> =>
    request<MyDleScoresResponse>('/dle-evaluation/my-scores'),

  /**
   * 指定した DLE の評価集計を取得する。
   * `VIEW_DLE_EVALUATION` が無いユーザーは 403 になる（UI 側でも `canViewOthers` で出し分ける）。
   */
  getScores: async (dle: string): Promise<MyDleScoresResponse> =>
    request<MyDleScoresResponse>(`/dle-evaluation/scores?dle=${encodeURIComponent(dle)}`),

  /**
   * メールアドレスで指定した DLE の評価集計を取得する。
   * メンバー詳細ページ (`/members/$email`) は email しか持たないため、nickname への解決は
   * サーバ側に任せる。nickName が引けない相手には `isDle: false` の空系列が返る。
   */
  getScoresByEmail: async (email: string): Promise<MyDleScoresResponse> =>
    request<MyDleScoresResponse>(`/dle-evaluation/scores?email=${encodeURIComponent(email)}`),
};
